import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Droplets, Gauge, CheckCircle2, AlertCircle, ShieldAlert,
  ArrowDown, Save, Beaker, FlaskConical, Thermometer, Timer,
  TrendingUp, BarChart3, Microscope, CircleDot,
} from "lucide-react";
import { useSampleLoader } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';

/* ─── Types ─────────────────────────────────────────────── */
type Configuration = 'outside_in_pressurised' | 'inside_out_pressurised' | 'submerged';
type FibreMaterial = 'pvdf' | 'pes' | 'pan';

interface UFParams {
  flowRate: number;        // m³/h net
  influentTurbidity: number; // NTU
  influentTSS: number;     // mg/L
  influentSDI: number;     // baseline SDI of upstream water
  influentFe: number;      // mg/L (precipitated floc carry-over)
  temperature: number;
  // Module
  configuration: Configuration;
  fibre: FibreMaterial;
  poreSize: number;        // µm
  fibreOD: number;         // mm
  fibreID: number;         // mm
  fibresPerModule: number;
  moduleArea: number;      // m² per module
  numModules: number;
  // Operation
  flux: number;            // L/m²·h (LMH)
  recovery: number;        // % overall
  backwashInterval: number; // min
  backwashDuration: number; // s
  cebInterval: number;     // h between CEB
  cipInterval: number;     // days between CIP
}

interface UFSize {
  totalArea: number;       // m²
  feedFlow: number;        // m³/h gross (including backwash)
  permeateFlow: number;    // m³/h
  actualFlux: number;      // LMH
  tmp: number;             // bar transmembrane pressure (clean)
  fluxNorm: number;        // 20°C normalised
  // Effluent
  outletSDI: number;
  outletTurbidity: number; // NTU
  particleRejectionPct: number;
  // Backwash and CEB
  bwWaterPctOfFeed: number; // %
  cebChemMonthlyKg: number;
  cipChemMonthlyKg: number;
  // Status
  status: 'optimal' | 'acceptable' | 'undersized' | 'overdesigned';
  statusDetail: string;
  foulingRisk: 'low' | 'moderate' | 'high';
  foulingReason: string;
}

const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

const FIBRE_PROPS: Record<FibreMaterial, { name: string; permeabilityBase: number; chlorineTolMax: number }> = {
  pvdf: { name: 'PVDF', permeabilityBase: 350, chlorineTolMax: 200 },
  pes:  { name: 'PES',  permeabilityBase: 320, chlorineTolMax: 50 },
  pan:  { name: 'PAN',  permeabilityBase: 280, chlorineTolMax: 5 },
};

/* ─── Calc engine ─── */
export function calcUF(p: UFParams): UFSize {
  const totalArea = p.moduleArea * p.numModules;
  const recoveryFrac = Math.max(0.5, Math.min(0.99, p.recovery / 100));
  const feedFlow = p.flowRate / recoveryFrac; // gross feed including BW losses
  const actualFlux = (p.flowRate * 1000) / totalArea; // LMH
  // Permeability at 20°C: ~p_base LMH/bar for PVDF clean; here we back-calc TMP
  const f = FIBRE_PROPS[p.fibre];
  const tempCorrection = Math.exp(0.0239 * (20 - p.temperature)); // viscosity correction
  const fluxNorm = actualFlux * tempCorrection;
  const tmp = fluxNorm / f.permeabilityBase;

  const permeateFlow = p.flowRate;

  // ─── Rejection: pore size vs particle population ───
  // At 0.02–0.04 µm, UF rejects essentially all particles >0.1 µm
  // Particle rejection ≈ 1 - (poreSize / particleD)²  (simplified Hagen-Poiseuille screening)
  // Inputs that drive SDI are mostly sub-µm; assume >99% rejection at 0.04 µm pore
  const particleRejectionPct = Math.max(95, Math.min(99.99, 100 - (p.poreSize / 0.5) * 5));

  // SDI out — UF reliably delivers SDI <2 from any feed with TSS <50 mg/L
  const sdiBase = 0.5 + (p.influentTSS / 200) * 0.5;
  const outletSDI = Math.min(3, sdiBase + Math.max(0, (actualFlux - 60) / 30) * 0.5);

  // Turbidity out: typically <0.1 NTU
  const outletTurbidity = Math.max(0.02, Math.min(0.2, p.influentTurbidity * 0.005));

  // Backwash water as % of feed
  const bwPerCycle = (p.flowRate / 60) * (p.backwashDuration / 60); // m³ per cycle
  const cyclesPerH = 60 / p.backwashInterval;
  const bwWaterPctOfFeed = (bwPerCycle * cyclesPerH / feedFlow) * 100;

  // CEB chemical consumption (rough)
  // NaOCl ~50 mg/L × CEB volume × cycles/month
  const cebCyclesPerMonth = (24 * 30) / Math.max(1, p.cebInterval);
  const cebVolPerCycle = bwPerCycle * 2; // ~2× normal bw
  const cebChemMonthlyKg = (cebCyclesPerMonth * cebVolPerCycle * 50) / 1e6 * 1000; // kg NaOCl/month equiv

  // CIP chemical
  const cipCyclesPerMonth = 30 / Math.max(1, p.cipInterval);
  const cipVolPerCycle = totalArea * 2 / 1000; // m³ (~2 L/m²)
  const cipChemMonthlyKg = cipCyclesPerMonth * cipVolPerCycle * 30; // ~3% acid/alkali

  // ─── Status ───
  let status: UFSize['status']; let statusDetail: string;
  if (actualFlux >= 30 && actualFlux <= 80 && tmp <= 0.7) {
    status = 'optimal';
    statusDetail = `Optimal. Flux ${actualFlux.toFixed(0)} LMH, TMP ${tmp.toFixed(2)} bar, SDI out ${outletSDI.toFixed(1)}.`;
  } else if (actualFlux <= 100 && tmp <= 1.0) {
    status = 'acceptable';
    statusDetail = `Acceptable. Flux ${actualFlux.toFixed(0)} LMH approaching design limit.`;
  } else if (actualFlux > 100 || tmp > 1.0) {
    status = 'undersized';
    statusDetail = `Undersized. Flux ${actualFlux.toFixed(0)} LMH / TMP ${tmp.toFixed(2)} bar too high. Add modules.`;
  } else {
    status = 'overdesigned';
    statusDetail = `Overdesigned. Consider fewer modules to save capex.`;
  }

  const foulingRisk: UFSize['foulingRisk'] =
    p.influentFe > 0.1 ? 'high' :
    actualFlux > 80 ? 'high' :
    p.influentTSS > 20 ? 'moderate' :
    'low';
  const foulingReason =
    p.influentFe > 0.1 ? `Fe carryover ${p.influentFe} mg/L will foul fibres — strengthen upstream catalytic stage.` :
    actualFlux > 80 ? `High flux drives rapid fouling. Reduce LMH or add area.` :
    p.influentTSS > 20 ? `Elevated TSS ${p.influentTSS} mg/L will shorten CEB interval.` :
    'Feed quality well within UF envelope.';

  return {
    totalArea, feedFlow, permeateFlow, actualFlux, tmp, fluxNorm,
    outletSDI, outletTurbidity, particleRejectionPct,
    bwWaterPctOfFeed, cebChemMonthlyKg, cipChemMonthlyKg,
    status, statusDetail, foulingRisk, foulingReason,
  };
}

const DEFAULTS: UFParams = {
  flowRate: 24, influentTurbidity: 0.3, influentTSS: 1, influentSDI: 4,
  influentFe: 0.02, temperature: 12,
  configuration: 'outside_in_pressurised', fibre: 'pvdf', poreSize: 0.03,
  fibreOD: 1.3, fibreID: 0.7, fibresPerModule: 9000, moduleArea: 50,
  numModules: 4,
  flux: 60, recovery: 92, backwashInterval: 30, backwashDuration: 60,
  cebInterval: 12, cipInterval: 30,
};

function Slider({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange, info }: any) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
        </div>
        <span className="text-xs font-bold text-white tabular-nums">
          {(() => { const v = Number.isFinite(value) ? value : 0; return step < 0.01 ? v.toFixed(3) : step < 0.1 ? v.toFixed(2) : step < 1 ? v.toFixed(1) : v.toFixed(0); })()}
          <span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span>
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full cursor-pointer"
        style={{ accentColor: color, color, ['--p' as any]: `${Math.max(0, Math.min(100, ((value - min) / Math.max(0.0001, (max - min))) * 100)).toFixed(2)}%` }} />
      {info && <p className="text-[10px] text-slate-600">{info}</p>}
    </div>
  );
}

function GaugeBar({ value, max, label, unit, color }: any) {
  const safeValue = Number.isFinite(value) ? value : 0;
  const safeMax = Number.isFinite(max) && max > 0 ? max : 1;
  const pct = Math.min(100, Math.max(0, (safeValue / safeMax) * 100));
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]">
        <span className="text-slate-500">{label}</span>
        <span className="font-bold" style={{ color }}>{safeValue.toFixed(2)} {unit}</span>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} className="h-full rounded-full" style={{ backgroundColor: color }} />
      </div>
    </div>
  );
}

const LS_KEY = 'rb_uf_spec';

export default function UFSpecPage() {
  const sampleLoader = useSampleLoader();
  const [params, setParams] = useState<UFParams>(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (stored) return { ...DEFAULTS, ...JSON.parse(stored) };
    } catch { /* ignore */ }
    return DEFAULTS;
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const sp = sampleLoader.baseSampleParams as any;
    if (!sp) return;
    setParams(p => ({
      ...p,
      flowRate: sp.flowRate ? sp.flowRate / 24 : p.flowRate,
      influentTurbidity: sp.turbidity ?? p.influentTurbidity,
      influentTSS: sp.tss ?? p.influentTSS,
      influentFe: sp.fe ?? sp.iron ?? p.influentFe,
      temperature: sp.temp ?? p.temperature,
    }));
  }, [sampleLoader.baseSampleParams]);

  const size = useMemo(() => calcUF(params), [params]);
  const update = <K extends keyof UFParams>(k: K, v: UFParams[K]) => setParams(p => ({ ...p, [k]: v }));

  const handleSave = () => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(params)); setSaved(true); toast.success('UF spec saved'); setTimeout(() => setSaved(false), 1500); }
    catch { toast.error('Save failed'); }
  };

  const statusColors: any = {
    optimal: { bg: 'bg-emerald-500/5', border: 'border-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
    acceptable: { bg: 'bg-sky-500/5', border: 'border-sky-500/20', text: 'text-sky-400', icon: CheckCircle2 },
    undersized: { bg: 'bg-rose-500/5', border: 'border-rose-500/20', text: 'text-rose-400', icon: ShieldAlert },
    overdesigned: { bg: 'bg-amber-500/5', border: 'border-amber-500/20', text: 'text-amber-400', icon: AlertCircle },
  };
  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;
  const f = FIBRE_PROPS[params.fibre];

  const scienceSections = [
    {
      title: 'Pore-size vs particle rejection',
      content: `UF with ${params.poreSize} µm pore physically blocks particles >0.1 µm (≈ 25× pore size for size-exclusion). For the Mylar borehole, 100% of particles measure <4 µm but most are 1–3 µm — UF rejects all of them. Particle rejection ${size.particleRejectionPct.toFixed(2)}%. Outlet turbidity ${size.outletTurbidity.toFixed(2)} NTU.`,
    },
    {
      title: 'SDI guarantee',
      content: `SDI is driven by sub-µm material that granular media cannot capture. UF removes this physically. With ${size.outletSDI.toFixed(1)} SDI achievable, downstream RO operates well within design (<3). This is the ONLY pre-treatment that delivers SDI guarantee without coagulation chemistry.`,
    },
    {
      title: 'Flux and TMP',
      content: `Operating flux ${size.actualFlux.toFixed(0)} LMH at ${params.temperature}°C → 20°C-normalised ${size.fluxNorm.toFixed(0)} LMH. Clean-fibre TMP ≈ ${size.tmp.toFixed(2)} bar (${f.name} membrane). Typical operating range 30–80 LMH; design at 60 LMH for sustainability. CEB every ${params.cebInterval}h, CIP every ${params.cipInterval}d.`,
    },
    {
      title: 'Iron carryover risk',
      content: `Catalytic media upstream must deliver Fe <0.1 mg/L into UF. At ${params.influentFe} mg/L Fe³⁺ floc, fouling rate is ${size.foulingRisk}. Iron oxides plug fibre lumens irreversibly; CIP recovery may not be complete after repeated exposure. Add inline cartridge polish (5 µm) between catalytic stage and UF as belt-and-braces.`,
    },
  ];

  return (
    <div className="space-y-5 px-3 py-4">
      <PageHeader
        title="Ultrafiltration (UF) Skid"
        subtitle="Hollow-fibre membrane barrier — guaranteed SDI <3 for RO pre-treatment"
        icon={CircleDot}
        right={<button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20"><Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}</button>}
      />

      <SampleSelector loader={sampleLoader} />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-7 space-y-4">
          <div className="p-4 rb-card-sm">
            <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
              <ArrowDown className="w-3 h-3" /> UF Skid — Hollow-Fibre Membrane Bank
            </p>
            <svg viewBox="0 0 900 360" className="w-full h-auto">
              <defs>
                <linearGradient id="waterGradU" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#0f2847" stopOpacity="0.8" />
                </linearGradient>
              </defs>
              <text x="450" y="18" fill="#94a3b8" fontSize="11" textAnchor="middle" fontWeight="bold">ULTRAFILTRATION SKID — SUB-MICRON BARRIER</text>

              {/* Influent */}
              <rect x="20" y="60" width="110" height="100" rx="6" fill="url(#waterGradU)" stroke="#38bdf8" strokeWidth="2" />
              <text x="75" y="78" fill="#38bdf8" fontSize="9" textAnchor="middle" fontWeight="bold">Feed</text>
              <text x="75" y="92" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(size.feedFlow, 1)} m³/h</text>
              <text x="75" y="104" fill="#94a3b8" fontSize="8" textAnchor="middle">SDI {fmt(params.influentSDI, 1)}</text>
              <text x="75" y="116" fill="#94a3b8" fontSize="8" textAnchor="middle">Turb {fmt(params.influentTurbidity, 2)} NTU</text>
              <text x="75" y="128" fill="#f43f5e" fontSize="8" textAnchor="middle">Fe {fmt(params.influentFe, 3)} mg/L</text>
              <text x="75" y="140" fill="#94a3b8" fontSize="8" textAnchor="middle">part. 1–3 µm</text>

              <line x1="132" y1="110" x2="200" y2="110" stroke="#38bdf8" strokeWidth="2" strokeDasharray="6 4" />

              {/* Feed pump */}
              <g transform="translate(200, 92)">
                <circle cx="18" cy="18" r="16" fill="#0f172a" stroke="#06b6d4" strokeWidth="1.5" />
                <line x1="18" y1="8" x2="18" y2="28" stroke="#06b6d4" strokeWidth="1.5" />
                <line x1="8" y1="18" x2="28" y2="18" stroke="#06b6d4" strokeWidth="1.5" />
                <text x="18" y="50" fill="#06b6d4" fontSize="7" textAnchor="middle">Feed pump</text>
              </g>

              <line x1="240" y1="110" x2="290" y2="110" stroke="#38bdf8" strokeWidth="2" strokeDasharray="6 4" />

              {/* UF module bank */}
              <rect x="295" y="50" width="280" height="180" rx="6" fill="url(#waterGradU)" stroke="#a855f7" strokeWidth="2" />
              <text x="435" y="42" fill="#a855f7" fontSize="9" textAnchor="middle" fontWeight="bold">{params.numModules}× UF modules · {f.name} {params.poreSize} µm</text>

              {/* Render modules */}
              {Array.from({length: Math.min(8, params.numModules)}).map((_, i) => {
                const cols = Math.min(4, Math.ceil(Math.min(8, params.numModules) / 2));
                const x = 305 + (i % cols) * 65;
                const y = 60 + Math.floor(i / cols) * 90;
                return (
                  <g key={i} transform={`translate(${x}, ${y})`}>
                    <rect x="0" y="0" width="55" height="80" rx="3" fill="#0f172a" stroke="#a855f7" strokeWidth="1" />
                    {/* Fibres */}
                    {Array.from({length: 14}).map((_, j) => (
                      <line key={j} x1={5 + j * 3.5} y1="5" x2={5 + j * 3.5} y2="75" stroke="#a855f7" strokeWidth="0.5" opacity="0.5" />
                    ))}
                    <text x="27" y="92" fill="#a855f7" fontSize="6" textAnchor="middle">M{i + 1}</text>
                  </g>
                );
              })}
              {params.numModules > 8 && (
                <text x="565" y="220" fill="#94a3b8" fontSize="7" textAnchor="end">+{params.numModules - 8} more</text>
              )}
              <text x="435" y="248" fill="#94a3b8" fontSize="8" textAnchor="middle">Total area {fmt(size.totalArea, 0)} m² · Flux {fmt(size.actualFlux, 0)} LMH · TMP {fmt(size.tmp, 2)} bar</text>

              {/* Permeate out */}
              <line x1="577" y1="110" x2="610" y2="110" stroke="#10b981" strokeWidth="2" strokeDasharray="6 4" />
              <rect x="615" y="65" width="110" height="100" rx="6" fill="url(#waterGradU)" stroke="#10b981" strokeWidth="2" />
              <text x="670" y="84" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Permeate</text>
              <text x="670" y="100" fill="#10b981" fontSize="8" textAnchor="middle">{fmt(size.permeateFlow, 1)} m³/h</text>
              <text x="670" y="114" fill="#10b981" fontSize="8" textAnchor="middle">SDI {fmt(size.outletSDI, 1)}</text>
              <text x="670" y="128" fill="#10b981" fontSize="8" textAnchor="middle">Turb {fmt(size.outletTurbidity, 2)} NTU</text>
              <text x="670" y="142" fill="#10b981" fontSize="8" textAnchor="middle">Rej. {fmt(size.particleRejectionPct, 2)}%</text>
              <text x="670" y="156" fill="#10b981" fontSize="7" textAnchor="middle">RO-ready ✓</text>

              {/* Reject / backwash */}
              <line x1="435" y1="230" x2="435" y2="270" stroke="#fbbf24" strokeWidth="1.5" strokeDasharray="3 3" />
              <rect x="370" y="270" width="130" height="40" rx="4" fill="#0f172a" stroke="#fbbf24" strokeWidth="1" />
              <text x="435" y="285" fill="#fbbf24" fontSize="8" textAnchor="middle">Reject / BW</text>
              <text x="435" y="300" fill="#94a3b8" fontSize="7" textAnchor="middle">{fmt(size.bwWaterPctOfFeed, 1)}% of feed → drain</text>

              {/* CEB / CIP */}
              <g transform="translate(745, 60)">
                <rect x="0" y="0" width="140" height="280" rx="6" fill="#0f172a" stroke="#1e293b" strokeWidth="1" />
                <text x="70" y="16" fill="#94a3b8" fontSize="9" textAnchor="middle" fontWeight="bold">PERFORMANCE</text>
                <text x="8" y="36" fill="#64748b" fontSize="8">Net flow</text>
                <text x="132" y="36" fill="#10b981" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(params.flowRate, 1)} m³/h</text>
                <text x="8" y="54" fill="#64748b" fontSize="8">Recovery</text>
                <text x="132" y="54" fill="#10b981" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(params.recovery, 0)}%</text>
                <text x="8" y="72" fill="#64748b" fontSize="8">Flux</text>
                <text x="132" y="72" fill="#06b6d4" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.actualFlux, 0)} LMH</text>
                <text x="8" y="90" fill="#64748b" fontSize="8">Flux 20°C</text>
                <text x="132" y="90" fill="#06b6d4" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.fluxNorm, 0)} LMH</text>
                <text x="8" y="108" fill="#64748b" fontSize="8">TMP (clean)</text>
                <text x="132" y="108" fill="#06b6d4" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.tmp, 2)} bar</text>
                <text x="8" y="126" fill="#64748b" fontSize="8">Total area</text>
                <text x="132" y="126" fill="#06b6d4" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.totalArea, 0)} m²</text>
                <text x="8" y="148" fill="#64748b" fontSize="8" fontWeight="bold">Effluent</text>
                <text x="8" y="164" fill="#64748b" fontSize="8">SDI₁₅</text>
                <text x="132" y="164" fill={size.outletSDI <= 3 ? '#10b981' : '#f43f5e'} fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.outletSDI, 1)}</text>
                <text x="8" y="180" fill="#64748b" fontSize="8">Turbidity</text>
                <text x="132" y="180" fill="#10b981" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.outletTurbidity, 2)} NTU</text>
                <text x="8" y="196" fill="#64748b" fontSize="8">Rejection</text>
                <text x="132" y="196" fill="#10b981" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.particleRejectionPct, 2)}%</text>
                <text x="8" y="218" fill="#64748b" fontSize="8" fontWeight="bold">Maintenance</text>
                <text x="8" y="234" fill="#64748b" fontSize="8">BW water</text>
                <text x="132" y="234" fill="#fbbf24" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.bwWaterPctOfFeed, 1)}%</text>
                <text x="8" y="250" fill="#64748b" fontSize="8">CEB chem</text>
                <text x="132" y="250" fill="#fbbf24" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.cebChemMonthlyKg, 1)} kg/mo</text>
                <text x="8" y="266" fill="#64748b" fontSize="8">CIP chem</text>
                <text x="132" y="266" fill="#fbbf24" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.cipChemMonthlyKg, 1)} kg/mo</text>
              </g>
            </svg>
          </div>
        </div>

        <div className="xl:col-span-5 space-y-5">
          <div className="mb-2 text-center">
            <p className="text-[10px] font-bold tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] uppercase">
              Reynolds &amp; Bauhm Smart Tool 2.0 — All Rights Reserved
            </p>
          </div>

          <div className={`rounded-xl border p-3 ${sc.bg} ${sc.border}`}>
            <div className="flex items-center gap-2">
              <StatusIcon className={`w-4 h-4 ${sc.text}`} />
              <span className={`text-xs font-bold uppercase ${sc.text}`}>{size.status}</span>
            </div>
            <p className={`text-[10px] mt-1 leading-relaxed ${sc.text} opacity-80`}>{size.statusDetail}</p>
          </div>

          {size.foulingRisk !== 'low' && (
            <div className="rounded-xl border p-3 bg-amber-500/5 border-amber-500/20">
              <div className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-400" /><span className="text-xs font-bold uppercase text-amber-400">Fouling risk: {size.foulingRisk}</span></div>
              <p className="text-[10px] mt-1 leading-relaxed text-amber-400 opacity-80">{size.foulingReason}</p>
            </div>
          )}

          {/* Configuration / material */}
          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Module Configuration</h3>
            <div>
              <p className="text-[10px] text-slate-500 mb-1">Configuration</p>
              <select value={params.configuration} onChange={e => update('configuration', e.target.value as Configuration)} className="w-full bg-rb-elevated border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200">
                <option value="outside_in_pressurised">Outside-in pressurised</option>
                <option value="inside_out_pressurised">Inside-out pressurised</option>
                <option value="submerged">Submerged (vacuum)</option>
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(FIBRE_PROPS) as FibreMaterial[]).map(k => {
                const fp = FIBRE_PROPS[k];
                const active = params.fibre === k;
                return (
                  <button key={k} onClick={() => update('fibre', k)} className={`text-left p-2 rounded-lg border transition-all ${active ? 'bg-purple-500/10 border-purple-500/30' : 'bg-rb-elevated border-slate-800 hover:border-slate-700'}`}>
                    <p className={`text-xs font-bold ${active ? 'text-purple-400' : 'text-slate-300'}`}>{fp.name}</p>
                    <p className="text-[9px] text-slate-500 leading-tight mt-0.5">Cl tol ≤{fp.chlorineTolMax} ppm·h</p>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Slider label="Pore size" value={params.poreSize} min={0.01} max={0.1} step={0.005} unit="µm" icon={Microscope} color="#a855f7" onChange={(v: number) => update('poreSize', v)} />
              <Slider label="Modules" value={params.numModules} min={1} max={16} step={1} unit="" icon={CircleDot} color="#a855f7" onChange={(v: number) => update('numModules', v)} />
              <Slider label="Area / module" value={params.moduleArea} min={20} max={80} step={5} unit="m²" icon={BarChart3} color="#a855f7" onChange={(v: number) => update('moduleArea', v)} />
              <Slider label="Fibre OD" value={params.fibreOD} min={0.5} max={2.5} step={0.05} unit="mm" icon={Microscope} color="#94a3b8" onChange={(v: number) => update('fibreOD', v)} />
            </div>
          </div>

          {/* Feed */}
          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Feed Quality</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Slider label="Flow (net)" value={params.flowRate} min={1} max={300} step={1} unit="m³/h" icon={Droplets} color="#38bdf8" onChange={(v: number) => update('flowRate', v)} />
              <Slider label="Turbidity in" value={params.influentTurbidity} min={0.01} max={50} step={0.05} unit="NTU" icon={FlaskConical} color="#38bdf8" onChange={(v: number) => update('influentTurbidity', v)} />
              <Slider label="TSS in" value={params.influentTSS} min={0} max={100} step={1} unit="mg/L" icon={Beaker} color="#38bdf8" onChange={(v: number) => update('influentTSS', v)} />
              <Slider label="SDI in (baseline)" value={params.influentSDI} min={1} max={6} step={0.1} unit="" icon={Gauge} color="#38bdf8" onChange={(v: number) => update('influentSDI', v)} />
              <Slider label="Fe carryover" value={params.influentFe} min={0} max={0.5} step={0.005} unit="mg/L" icon={FlaskConical} color="#f43f5e" onChange={(v: number) => update('influentFe', v)} />
              <Slider label="Temperature" value={params.temperature} min={2} max={30} step={1} unit="°C" icon={Thermometer} color="#06b6d4" onChange={(v: number) => update('temperature', v)} />
            </div>
          </div>

          {/* Operation */}
          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Operation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Slider label="Design Flux" value={params.flux} min={20} max={120} step={1} unit="LMH" icon={TrendingUp} color="#06b6d4" onChange={(v: number) => update('flux', v)} />
              <Slider label="Recovery" value={params.recovery} min={75} max={99} step={0.5} unit="%" icon={Gauge} color="#10b981" onChange={(v: number) => update('recovery', v)} />
              <Slider label="BW interval" value={params.backwashInterval} min={15} max={120} step={5} unit="min" icon={Timer} color="#fbbf24" onChange={(v: number) => update('backwashInterval', v)} />
              <Slider label="BW duration" value={params.backwashDuration} min={20} max={120} step={5} unit="s" icon={Timer} color="#fbbf24" onChange={(v: number) => update('backwashDuration', v)} />
              <Slider label="CEB interval" value={params.cebInterval} min={2} max={48} step={1} unit="h" icon={Timer} color="#a855f7" onChange={(v: number) => update('cebInterval', v)} />
              <Slider label="CIP interval" value={params.cipInterval} min={7} max={90} step={1} unit="d" icon={Timer} color="#a855f7" onChange={(v: number) => update('cipInterval', v)} />
            </div>
          </div>

          {/* Gauges */}
          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-200">Performance Gauges</h3>
            <div className="grid grid-cols-2 gap-3">
              <GaugeBar value={size.actualFlux} max={100} label="Flux" unit="LMH" color="#06b6d4" />
              <GaugeBar value={size.tmp} max={1.0} label="TMP" unit="bar" color={size.tmp > 0.7 ? '#f43f5e' : '#06b6d4'} />
              <GaugeBar value={size.outletSDI} max={3} label="Outlet SDI" unit="" color={size.outletSDI <= 3 ? '#10b981' : '#f43f5e'} />
              <GaugeBar value={size.particleRejectionPct} max={100} label="Particle rejection" unit="%" color="#10b981" />
            </div>
          </div>

          <div className="p-4 rb-card space-y-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Beaker className="w-4 h-4 text-sky-400" />Science Validation</h3>
            <div className="space-y-3">
              {scienceSections.map((s, i) => (
                <details key={i} className="rounded-lg border border-slate-800 bg-rb-elevated/40">
                  <summary className="cursor-pointer p-3 text-xs font-bold text-sky-400 hover:text-sky-300">{s.title}</summary>
                  <p className="px-3 pb-3 text-[11px] text-slate-300 leading-relaxed">{s.content}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
